import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Folder, Tag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Category {
  name: string;
  description: string;
  color: string;
  questionCount: number;
}

interface CategoryManagerProps {
  onCategoriesChange: () => void;
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  onCategoriesChange,
  selectedCategory,
  onCategorySelect
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });
  const { toast } = useToast();

  const QUESTIONS_POOL_TABLE_ID = '16057';

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      // Get all questions to extract unique categories
      const { data, error } = await window.ezsite.apis.tablePage(QUESTIONS_POOL_TABLE_ID, {
        PageNo: 1,
        PageSize: 1000, // Get all questions to count categories
        OrderByField: 'subject',
        IsAsc: true,
        Filters: []
      });

      if (error) throw error;

      // Count questions by category (subject)
      const categoryCount: {[key: string]: number;} = {};
      data.List.forEach((question: any) => {
        if (question.subject) {
          categoryCount[question.subject] = (categoryCount[question.subject] || 0) + 1;
        }
      });

      // Convert to category objects with default colors
      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];
      const categoriesData = Object.entries(categoryCount).map(([name, count], index) => ({
        name,
        description: `Questions in ${name}`,
        color: colors[index % colors.length],
        questionCount: count
      }));

      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Error",
        description: "Failed to fetch categories",
        variant: "destructive"
      });
    }
  };

  const handleAddCategory = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Category name is required",
        variant: "destructive"
      });
      return;
    }

    try {
      // For now, we'll just update the local state
      // In a real implementation, you might want to create a categories table
      const newCategory: Category = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        questionCount: 0
      };

      setCategories((prev) => [...prev, newCategory]);
      resetForm();
      setIsAddDialogOpen(false);
      onCategoriesChange();

      toast({
        title: "Success",
        description: "Category added successfully"
      });
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: "Error",
        description: "Failed to add category",
        variant: "destructive"
      });
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory || !formData.name.trim()) return;

    try {
      // Update category in local state
      setCategories((prev) => prev.map((cat) =>
      cat.name === editingCategory.name ?
      { ...cat, name: formData.name, description: formData.description, color: formData.color } :
      cat
      ));

      resetForm();
      setEditingCategory(null);
      onCategoriesChange();

      toast({
        title: "Success",
        description: "Category updated successfully"
      });
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    try {
      const category = categories.find((c) => c.name === categoryName);
      if (category && category.questionCount > 0) {
        toast({
          title: "Cannot Delete",
          description: "Cannot delete category with existing questions",
          variant: "destructive"
        });
        return;
      }

      setCategories((prev) => prev.filter((cat) => cat.name !== categoryName));
      onCategoriesChange();

      toast({
        title: "Success",
        description: "Category deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6'
    });
  };

  const openEditDialog = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      color: category.color
    });
  };

  return (
    <Card data-id="pxpgj75pv" data-path="src/components/questions/CategoryManager.tsx">
      <CardHeader data-id="vwei2bk4d" data-path="src/components/questions/CategoryManager.tsx">
        <CardTitle className="flex items-center gap-2" data-id="11fk6t1iq" data-path="src/components/questions/CategoryManager.tsx">
          <Folder className="h-5 w-5" data-id="xgcn8oaj4" data-path="src/components/questions/CategoryManager.tsx" />
          Categories
        </CardTitle>
        <CardDescription data-id="0cebi5e2x" data-path="src/components/questions/CategoryManager.tsx">
          Organize your questions by subject categories
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4" data-id="nzwp70qlg" data-path="src/components/questions/CategoryManager.tsx">
        {/* Category Filter Buttons */}
        <div className="flex flex-wrap gap-2" data-id="pj3l7aeh8" data-path="src/components/questions/CategoryManager.tsx">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onCategorySelect('all')} data-id="r8mcz4fp4" data-path="src/components/questions/CategoryManager.tsx">

            All Categories
          </Button>
          {categories.map((category) =>
          <Button
            key={category.name}
            variant={selectedCategory === category.name ? 'default' : 'outline'}
            size="sm"
            onClick={() => onCategorySelect(category.name)}
            className="flex items-center gap-2" data-id="gsba57hx9" data-path="src/components/questions/CategoryManager.tsx">

              <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: category.color }} data-id="8ye4k2cps" data-path="src/components/questions/CategoryManager.tsx" />

              {category.name}
              <Badge variant="secondary" className="text-xs" data-id="c5m1w7cf8" data-path="src/components/questions/CategoryManager.tsx">
                {category.questionCount}
              </Badge>
            </Button>
          )}
        </div>

        {/* Add Category Button */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} data-id="3df8dwiac" data-path="src/components/questions/CategoryManager.tsx">
          <DialogTrigger asChild data-id="y1v1m95o2" data-path="src/components/questions/CategoryManager.tsx">
            <Button variant="outline" size="sm" onClick={resetForm} data-id="kj135h7q5" data-path="src/components/questions/CategoryManager.tsx">
              <Plus className="h-4 w-4 mr-2" data-id="wbvos50fv" data-path="src/components/questions/CategoryManager.tsx" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent data-id="6j93cgf2i" data-path="src/components/questions/CategoryManager.tsx">
            <DialogHeader data-id="s2azln9ud" data-path="src/components/questions/CategoryManager.tsx">
              <DialogTitle data-id="6p1lwad4k" data-path="src/components/questions/CategoryManager.tsx">Add New Category</DialogTitle>
              <DialogDescription data-id="7wl8r7wff" data-path="src/components/questions/CategoryManager.tsx">
                Create a new category to organize your questions
              </DialogDescription>
            </DialogHeader>
            <CategoryForm
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleAddCategory}
              onCancel={() => setIsAddDialogOpen(false)} data-id="1iefjih3d" data-path="src/components/questions/CategoryManager.tsx" />

          </DialogContent>
        </Dialog>

        {/* Categories List */}
        <div className="space-y-2" data-id="52wjruyhq" data-path="src/components/questions/CategoryManager.tsx">
          {categories.map((category) =>
          <div
            key={category.name}
            className="flex items-center justify-between p-3 border rounded-lg" data-id="f5mi0cmmj" data-path="src/components/questions/CategoryManager.tsx">

              <div className="flex items-center gap-3" data-id="gjsrxxc06" data-path="src/components/questions/CategoryManager.tsx">
                <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: category.color }} data-id="qy94knbo2" data-path="src/components/questions/CategoryManager.tsx" />

                <div data-id="ibmrnrds1" data-path="src/components/questions/CategoryManager.tsx">
                  <p className="font-medium" data-id="trrytzgob" data-path="src/components/questions/CategoryManager.tsx">{category.name}</p>
                  <p className="text-sm text-gray-600" data-id="5jjbym5z5" data-path="src/components/questions/CategoryManager.tsx">{category.description}</p>
                </div>
                <Badge variant="secondary" data-id="v31krqxp0" data-path="src/components/questions/CategoryManager.tsx">
                  {category.questionCount} questions
                </Badge>
              </div>
              <div className="flex gap-1" data-id="2mm9hkvw4" data-path="src/components/questions/CategoryManager.tsx">
                <Dialog data-id="ahbjn782e" data-path="src/components/questions/CategoryManager.tsx">
                  <DialogTrigger asChild data-id="4g2s1ms7m" data-path="src/components/questions/CategoryManager.tsx">
                    <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(category)} data-id="6lw7u1mqo" data-path="src/components/questions/CategoryManager.tsx">

                      <Edit className="h-4 w-4" data-id="cm21zn39f" data-path="src/components/questions/CategoryManager.tsx" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-id="xxgxrgmge" data-path="src/components/questions/CategoryManager.tsx">
                    <DialogHeader data-id="bcmh3atsp" data-path="src/components/questions/CategoryManager.tsx">
                      <DialogTitle data-id="lx2y9on6f" data-path="src/components/questions/CategoryManager.tsx">Edit Category</DialogTitle>
                      <DialogDescription data-id="kw68cipqh" data-path="src/components/questions/CategoryManager.tsx">
                        Update category details
                      </DialogDescription>
                    </DialogHeader>
                    <CategoryForm
                    formData={formData}
                    setFormData={setFormData}
                    onSubmit={handleEditCategory}
                    onCancel={() => setEditingCategory(null)}
                    isEditing={true} data-id="s9lwf6uki" data-path="src/components/questions/CategoryManager.tsx" />

                  </DialogContent>
                </Dialog>

                <AlertDialog data-id="nqkxcv8u0" data-path="src/components/questions/CategoryManager.tsx">
                  <AlertDialogTrigger asChild data-id="3wyl53nvt" data-path="src/components/questions/CategoryManager.tsx">
                    <Button variant="ghost" size="sm" data-id="rs3hyo7mm" data-path="src/components/questions/CategoryManager.tsx">
                      <Trash2 className="h-4 w-4" data-id="0oq5bazs9" data-path="src/components/questions/CategoryManager.tsx" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-id="62bf3qalx" data-path="src/components/questions/CategoryManager.tsx">
                    <AlertDialogHeader data-id="fhgcp4tgr" data-path="src/components/questions/CategoryManager.tsx">
                      <AlertDialogTitle data-id="4x0jmnjwo" data-path="src/components/questions/CategoryManager.tsx">Delete Category</AlertDialogTitle>
                      <AlertDialogDescription data-id="vtn8yjfwr" data-path="src/components/questions/CategoryManager.tsx">
                        Are you sure you want to delete this category? 
                        {category.questionCount > 0 &&
                      <span className="text-red-600 font-medium" data-id="iiyz6m1g1" data-path="src/components/questions/CategoryManager.tsx">
                            {' '}This category has {category.questionCount} questions and cannot be deleted.
                          </span>
                      }
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter data-id="1mlpqtlve" data-path="src/components/questions/CategoryManager.tsx">
                      <AlertDialogCancel data-id="7oxmjui4f" data-path="src/components/questions/CategoryManager.tsx">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                      onClick={() => handleDeleteCategory(category.name)}
                      disabled={category.questionCount > 0} data-id="rpkwl3cri" data-path="src/components/questions/CategoryManager.tsx">

                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>);

};

// Category Form Component
interface CategoryFormProps {
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  isEditing = false
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const predefinedColors = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];


  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-id="5fk8fay17" data-path="src/components/questions/CategoryManager.tsx">
      <div className="space-y-2" data-id="1cc81iocv" data-path="src/components/questions/CategoryManager.tsx">
        <Label htmlFor="category_name" data-id="47ocy6xzw" data-path="src/components/questions/CategoryManager.tsx">Category Name *</Label>
        <Input
          id="category_name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="e.g., Mathematics, Science" data-id="xro2yf94u" data-path="src/components/questions/CategoryManager.tsx" />

      </div>

      <div className="space-y-2" data-id="xl4tmln9d" data-path="src/components/questions/CategoryManager.tsx">
        <Label htmlFor="category_description" data-id="xhhxwdnfx" data-path="src/components/questions/CategoryManager.tsx">Description</Label>
        <Input
          id="category_description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of this category" data-id="jqfc5r2p8" data-path="src/components/questions/CategoryManager.tsx" />

      </div>

      <div className="space-y-2" data-id="mxhtsttnl" data-path="src/components/questions/CategoryManager.tsx">
        <Label data-id="xcom84bg6" data-path="src/components/questions/CategoryManager.tsx">Color</Label>
        <div className="flex gap-2" data-id="srp2t4t4w" data-path="src/components/questions/CategoryManager.tsx">
          {predefinedColors.map((color) =>
          <button
            key={color}
            type="button"
            className={`w-8 h-8 rounded-full border-2 ${
            formData.color === color ? 'border-gray-800' : 'border-gray-300'}`
            }
            style={{ backgroundColor: color }}
            onClick={() => setFormData({ ...formData, color })} data-id="lo40225d0" data-path="src/components/questions/CategoryManager.tsx" />

          )}
        </div>
        <Input
          type="color"
          value={formData.color}
          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
          className="w-20" data-id="d95fgzfs4" data-path="src/components/questions/CategoryManager.tsx" />

      </div>

      <div className="flex justify-end gap-2" data-id="j7tfpjrot" data-path="src/components/questions/CategoryManager.tsx">
        <Button type="button" variant="outline" onClick={onCancel} data-id="61sykht8z" data-path="src/components/questions/CategoryManager.tsx">
          Cancel
        </Button>
        <Button type="submit" data-id="o1xwdm931" data-path="src/components/questions/CategoryManager.tsx">
          {isEditing ? 'Update Category' : 'Add Category'}
        </Button>
      </div>
    </form>);

};

export default CategoryManager;